import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Card, Collapse, Nav, Tab } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faChevronDown, faChevronRight, faUsers } from '@fortawesome/pro-solid-svg-icons';
import BuildoutLogo from 'logos/buildout-icon-only-latest.svg';

import { getPropertyTypesById } from 'helpers/property_types';
import { post, put } from 'helpers/api';

import Access from './access';
import PropertyTypes from './property_types';

const tabs = {
  users: {
    icon: faUsers,
    key: 'users',
    label: 'Users',
  },
  propertyTypes: {
    icon: faBuilding,
    key: 'propertyTypes',
    label: 'Property Types',
  },
};

export default function CompanyGridSettings({ company, companyGrid: initialCompanyGrid, companyUsers, paths }) {
  const initialPropertyTypesById = getPropertyTypesById();
  for (const propertyTypeId of initialCompanyGrid.enabledPropertyTypeIds) {
    const propertyType = initialPropertyTypesById[propertyTypeId];
    propertyType.enabled = true;

    for (const subtype of Object.values(propertyType.subtypesById)) {
      subtype.enabled = initialCompanyGrid.enabledPropertySubtypeIds.indexOf(subtype.id) >= 0;
    }
  }

  const [open, setOpen] = useState(false);
  const [companyGrid, setCompanyGrid] = useState({
    createShadowOnPropertyCreate: initialCompanyGrid.createShadowOnPropertyCreate,
    enabled: initialCompanyGrid.enabled,
    enabledForAllUsers: !initialCompanyGrid.filterUsers,
    enabledUserIds: initialCompanyGrid.allowedUserIds,
    gridId: initialCompanyGrid.gridId,
    name: initialCompanyGrid.name,
    internal: initialCompanyGrid.account.internal,
    propertyTypesById: initialPropertyTypesById,
  });

  function changeCompanyGrid(changes) {
    setCompanyGrid({ ...companyGrid, ...changes });
  }

  const pathRef = useRef();
  React.useEffect(() => {
    if (!pathRef.current) {
      pathRef.current = initialCompanyGrid.editPath || paths.companyGridCreate;
      return;
    }

    const enabledPropertyTypeIds = [];
    const enabledPropertySubtypeIds = [];
    for (const propertyType of Object.values(companyGrid.propertyTypesById)) {
      if (propertyType.enabled) {
        enabledPropertyTypeIds.push(propertyType.id);
      }

      for (const subtype of Object.values(propertyType.subtypesById)) {
        if (subtype.enabled) {
          enabledPropertySubtypeIds.push(subtype.id);
        }
      }
    }

    const method = pathRef.current === paths.companyGridCreate ? post : put;
    method(pathRef.current, {
      allowedUserIds: companyGrid.enabledUserIds,
      companyId: company.id,
      createShadowOnPropertyCreate: companyGrid.createShadowOnPropertyCreate,
      enabled: companyGrid.enabled,
      enabledPropertyTypeIds,
      enabledPropertySubtypeIds,
      filterUsers: !companyGrid.enabledForAllUsers,
      gridId: companyGrid.gridId,
    }).then((response) => {
      pathRef.current = response.editPath;
    });
  }, [companyGrid]);

  return (
    <Card>
      <Card.Header
        className="d-flex justify-content-between align-items-center clickable"
        onClick={() => setOpen(!open)}
      >
        <h6>{companyGrid.name}</h6>
        <div className="d-flex align-items-center gap-2">
          {companyGrid.internal &&
            <img alt="Internal grid" src={BuildoutLogo} style={{ height: 24 }} />
          }
          <FontAwesomeIcon fixedWidth icon={open ? faChevronDown : faChevronRight} />
        </div>
      </Card.Header>

      <Collapse in={open}>
        <Card.Body className="p-0">
          <Tab.Container
            defaultActiveKey={tabs.users.key}
            id={`grid-${companyGrid.gridId}-setttings`}
            transition={false}
          >
            <Nav className="bg-white" variant="tabs">
              {Object.values(tabs).map(tab => (
                <Nav.Item key={tab.key}>
                  <Nav.Link eventKey={tab.key}>
                    <FontAwesomeIcon className="me-2" icon={tab.icon} size="lg" />
                    {tab.label}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>

            <Tab.Content>
              <Tab.Pane eventKey={tabs.users.key}>
                <div className="d-flex flex-column gap-4">
                  <Access companyGrid={companyGrid} users={companyUsers} onChange={changeCompanyGrid} />
                </div>
              </Tab.Pane>

              <Tab.Pane eventKey={tabs.propertyTypes.key}>
                <div className="d-flex flex-column gap-4">
                  <PropertyTypes companyGrid={companyGrid} onChange={changeCompanyGrid} />
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Collapse>
    </Card>
  );
}

CompanyGridSettings.propTypes = {
  company: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  companyGrid: PropTypes.shape({
    account: PropTypes.shape({
      internal: PropTypes.bool,
    }),
    allowedUserIds: PropTypes.arrayOf(PropTypes.number),
    editPath: PropTypes.string,
    enabled: PropTypes.bool.isRequired,
    enabledPropertyTypeIds: PropTypes.arrayOf(PropTypes.number),
    filterUsers: PropTypes.bool,
    gridId: PropTypes.number.isRequired,
    name: PropTypes.string,
  }),
  companyUsers: PropTypes.array.isRequired,
  paths: PropTypes.shape({
    companyGridCreate: PropTypes.string.isRequired,
  }).isRequired
};
